/**
 * Pure model of the curator cabinet (wave 9, contract #26–#26.6, SR v2.1 §3.4).
 *
 * Framework-free on purpose (see `./exports-model`): the `unit` Playwright project
 * imports this file in plain Node, so nothing here may value-import `./client` —
 * the transport lives in `./curator`, this file owns every DECISION:
 *
 *   - the DTO mirrors of the generated `api-types.ts` (kept local for the same
 *     reason `pages/dashboard/status.ts` repeats the status union — no bundler in
 *     the unit project),
 *   - the query the list read sends (filters + paging),
 *   - the D6 section split (offline vs online BY ASSIGNMENT — what a venue carries,
 *     not what a user is called),
 *   - the D5/D11 status rule and the miss-reason wording,
 *   - the RU copy of every refusal the cabinet can meet.
 *
 * NO CONTACT FIELDS EXIST ON ANY TYPE HERE (A12/D9): the projection is a server
 * property, and a type that has no `phone`/`email` key cannot render one.
 */

// The RU status vocabulary comes from the dashboard's map (T.R1: one map, every
// page). That module only TYPE-imports its api module, so this value import keeps
// this file plain-Node importable for the `unit` project.
// ADDITIVE (v1.8.8, T.Z43) — the audience vocabulary, one definition shared with the api.
import { audienceFilterOf } from '@cb-manager/shared';

import { DASHBOARD_STATUSES, STATUS_LABEL, STATUS_TONE } from '../pages/dashboard/status';
// T.Z24 — the natural-key 409 of #26.3/#26.5 is THE duplicate refusal, so it shares
// the drawer's hard-block sentence. The one constant is all this file takes from the
// pages/ tree (same discipline as the status map above); `prefill-duplicates` itself
// imports nothing, so no cycle.
import { PREFILL_DUPLICATE_BLOCK_MESSAGE } from '../pages/cabinet/prefill-duplicates';
import type { DashboardStatus } from './dashboard';

// ---------------------------------------------------------------------------
// DTO mirrors — 1:1 with `CuratorVenueDto` / `CuratorPrefillDto` / `CuratorChildDto`
// ---------------------------------------------------------------------------

export type CuratorVenueKind = 'REGISTRY' | 'PARTNER_ONLINE';

export interface CuratorPartnerRef {
  code: string;
  /** The SHARED pool of the partner — the same number every sibling branch shows. */
  planOnline: number;
  /** Live prefill rows counted against the pool across ALL the partner's branches. */
  used: number;
}

export interface CuratorOfflineSummary {
  planOffline: number;
  offlineAssigned: number;
}

export interface CuratorOnlineSummary {
  prefillTotal: number;
  found: number;
  notFound: number;
  ambiguous: number;
  /**
   * ADDITIVE (v1.9.26, T.Z125) — the venue's ONLINE-set children the working set does
   * not hold (`prefillTotal + onlineAssigned` = the online table's `total`). An older
   * api that does not publish the key reads as 0 — the working rows alone, the
   * pre-v1.9.26 table exactly.
   */
  onlineAssigned: number;
  /**
   * ADDITIVE (v1.9.26, T.Z125) — the venue's OWN online quota (`venue_plans.plan_online`),
   * 0 = none assigned; the mirror of the offline half's `planOffline`. NOT the partner
   * pool (that one stays on `partner`).
   */
  planOnline: number;
}

export interface CuratorVenue {
  venueId: number;
  name: string;
  region: string | null;
  kind: CuratorVenueKind;
  /** The D3 flag — what turns the three prefill writes and the online section on. */
  allowCuratorChildren: boolean;
  partner: CuratorPartnerRef | null;
  summary: {
    offline: CuratorOfflineSummary | null;
    online: CuratorOnlineSummary | null;
  };
  lastEviSnapshotAt: string | null;
  lastUntiSyncAt: string | null;
}

export interface CuratorChild {
  /**
   * v1.9.32 (T.Z139) — `null` = the child has no ФИО YET: the name is ЕВИ-owned and
   * the ЕПГУ apply writes none, so a child still before the ЕВИ letter rides its own
   * roster row without the key. The cabinet renders «—» for it; the api orders such
   * rows after the named ones, so the table's order stays deterministic.
   */
  fullName: string | null;
  region: string | null;
  programName: string | null;
  currentStatus: string;
  pguId: string;
  untiId: string | null;
  /** ADDITIVE (v1.9.5, T.Z50) — the handle the «Группы» surface assigns a child by. */
  applicationId: number | null;
  /** ADDITIVE (v1.9.6, T.Z62) — the child's учебная группа, `null` = none. */
  group: CuratorGroupRef | null;
  /**
   * ADDITIVE (v1.9.64, T.Z195) — the chase facts («Договор», «Дедлайн договора»,
   * «Зачислен», «Не перешёл»), the SAME shapes #3's `ApplicationDto` carries: the
   * contract fold + its signing day, the register's derived clock (the api computes
   * it, the table only formats), the cat.2035 word and the two «Провалил ЕВИ» facts
   * the UI folds into ONE chip. NO `signerName` — the signer's identity is a
   * managers-only PII decision. Optional with `| null`: an api that predates the
   * keys (or a hand-built stub) reads as «no fact», which is exactly what the cell
   * renders.
   */
  contractState?: 'SIGNED' | 'NOT_SIGNED' | 'NOT_ARRIVED' | null;
  contractSignedAt?: string | null;
  contractDeadlineAt?: string | null;
  contractDaysLeft?: number | null;
  enrolled?: boolean | null;
  eviFailed?: boolean;
  eviFailedManually?: boolean;
}

export type CuratorMatchStatus = 'NOT_FOUND' | 'MATCHED' | 'AMBIGUOUS';
export type CuratorMissReason = 'NO_ID_AND_NO_NAME_HIT' | 'REGION_UNMAPPED' | 'NAME_FORMAT' | 'AMBIGUOUS';

/**
 * ADDITIVE (contract v1.9.26, T.Z125) — which population of the widened #26.2 a row
 * belongs to. `'PREFILL'` is the working row the table always answered; `'ONLINE_ROSTER'`
 * is the venue's ONLINE-set application row (read-only; the row IS the application).
 */
export type PrefillPopulation = 'PREFILL' | 'ONLINE_ROSTER';

/** The one test a row must pass before the UI may offer a write on it (T.Z125). */
export function isPrefillWorkingRow(row: CuratorPrefillRow): boolean {
  return (row.population ?? 'PREFILL') === 'PREFILL';
}

/**
 * ADDITIVE (contract v1.9.6, T.Z62) — the учебная группа a child sits in, as the
 * rows carry it: the caption plus the #26.12/#26.13 handle. Not the whole group —
 * the roster and the counts stay on #26.8.
 */
export interface CuratorGroupRef {
  id: number;
  name: string;
}

export interface CuratorPrefillRow {
  id: number;
  fullName: string;
  nameFormatOk: boolean;
  region: string | null;
  programName: string | null;
  pguId: string | null;
  untiId: string | null;
  /** Present only on a MATCHED row (D11) — the child's CURRENT system status. */
  currentStatus?: string;
  /**
   * ADDITIVE (contract v1.8.8, T.Z43) — the row's RESOLVED course
   * (`curator_children.course_id`), `null` when the file's (programme, format,
   * audience) triple matched nothing. The audience/format a row shows are the
   * COURSE's properties (#21 by this id), never fields of the row.
   */
  courseId?: number | null;
  /**
   * ADDITIVE (contract v1.9.6, T.Z62) — the matched child's `applications.id`: the
   * handle #26.12/#26.13 assign/remove by. `null` while the row is unmatched — the
   * rule the drawer's disabled «Группа» select states.
   */
  applicationId: number | null;
  /** ADDITIVE (contract v1.9.6, T.Z62) — the child's учебная группа, `null` = none. */
  group: CuratorGroupRef | null;
  /**
   * ADDITIVE (contract v1.9.26, T.Z125) — the population the row belongs to.
   * `'PREFILL'` (default, and what an older api answers): the curator's working row,
   * editable per the server's `canEdit`/`canDelete`. `'ONLINE_ROSTER'`: the venue's
   * ONLINE-set application row the widened #26.2 streams next to them — read-only by
   * the server's own `canEdit/canDelete: false`, always with a `currentStatus`, and
   * its `id` mirrors `applicationId` (the #26.12/#26.13 handle; the row-editing
   * endpoints do not know it).
   */
  population?: PrefillPopulation;
  /** Present only on an unmatched row (D5); `MATCHED` never travels to the cabinet. */
  matchStatus?: Exclude<CuratorMatchStatus, 'MATCHED'>;
  /** The coarse miss reason of an unmatched row (v2.1). */
  missReason?: CuratorMissReason;
  /**
   * ADDITIVE (contract v1.9.46, T.Z166) — the format of the application the row is
   * MATCHED to, `null` while it is unmatched. `'OFFLINE'` is the cabinet's офлайн
   * flag: the child already stands in an офлайн-таблице, and the table shows the row
   * red with the «удалите из онлайн-набора» hint (the remove stays the curator's
   * action). An `'ONLINE_ROSTER'` row IS an ONLINE application and keeps the key off.
   */
  matchedApplicationFormat?: 'ONLINE' | 'OFFLINE' | null;
  /**
   * ADDITIVE (contract v1.9.64, T.Z195) — the chase facts of the MATCHED child (an
   * `'ONLINE_ROSTER'` row's own). Same shapes and rest values as
   * {@link CuratorChild}'s: an unmatched row answers null / null / null / null /
   * null / false / false, and an api that predates the keys reads the same. NO
   * `signerName` — a curator surface never carries the signer's identity.
   */
  contractState?: 'SIGNED' | 'NOT_SIGNED' | 'NOT_ARRIVED' | null;
  contractSignedAt?: string | null;
  contractDeadlineAt?: string | null;
  contractDaysLeft?: number | null;
  enrolled?: boolean | null;
  eviFailed?: boolean;
  eviFailedManually?: boolean;
  /**
   * ADDITIVE (contract v1.9.83, T.Z227; AMENDED v1.9.84 — the same-day hotfix) — the
   * child's contacts, the OWNER-RULED ONLINE-surface widening (owner 2026-10-05,
   * «только в кабинеты кураторам ОНЛАЙН»). Since v1.9.84 the keys are a property of
   * the SURFACE, not of one population: an `'ONLINE_ROSTER'` row carries its OWN
   * contacts, a `'PREFILL'` row the MATCHED child's (the owner's live check caught
   * v1.9.83 printing «—» on exactly the rows the curator looks at — a filial's
   * uploaded roster IS a matched working row), `null` = nothing is known (unmatched,
   * or the export held nothing) — the «—» cell. The offline roster (`CuratorChild`)
   * still has NO contact key at all, so the «слепок» holds there by construction.
   */
  phone?: string | null;
  email?: string | null;
  lastCheckedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

/** The pagination block #26.2 answers (`meta`). */
export interface CuratorPageMeta {
  page: number;
  pageSize: number;
  total: number;
  /**
   * ADDITIVE (contract v1.9.46, T.Z166) — how many of the scope's LIVE working rows
   * are matched to an application with `format = 'OFFLINE'`, filters and pagination
   * ignored. The number the «Онлайн-набор» top notice speaks; `undefined` on an api
   * that predates the key (the notice then simply does not render).
   */
  offlineFormatMatched?: number;
}

// ---------------------------------------------------------------------------
// Paths + query
// ---------------------------------------------------------------------------

export const CURATOR_VENUES_PATH = '/curator/venues';
export const CURATOR_CHILDREN_OFFLINE_PATH = '/curator/children-offline';
export const CURATOR_PREFILL_PATH = '/curator/prefill';
export const CURATOR_EXPORT_PATH = '/curator/export';

/**
 * Filters of #26.2 as the URL carries them (`?venueId=&status=&q=`).
 *
 * `nameFormat` is NO LONGER SENT (T.Z14/C3): the ФИО-format filter left the UI, so
 * the SPA stops offering the parameter — the api keeps answering it (the contract is
 * additive-only), and the «ФИО не в формате» data stays where it belongs, on the
 * ADMIN prefill registry (#27).
 */
export interface PrefillFilters {
  venueId?: number;
  /**
   * ADDITIVE (contract v1.8.1) — the two match states the api always answered, plus
   * the six funnel statuses, which select the MATCHED rows by the very
   * `currentStatus` the table shows ({@link rowStatusView}). One vocabulary, no
   * client-side re-derivation.
   *
   * The SELECT offers the funnel only (T.Z14/C6): a single wire value cannot say
   * «NOT_FOUND or AMBIGUOUS», and the merged «Не найдено» bucket is the summary
   * chips' job — the one place the UI still speaks about match states.
   */
  status?: PrefillStatusFilter;
  /**
   * ADDITIVE (contract v1.8.2) — the working table's search box. One free-text OR
   * over the ФИО (normalised contains) and the two ids (exact or prefix), resolved
   * SERVER-side; the SPA only trims and passes it through.
   */
  q?: string;
  /**
   * ADDITIVE (contract v1.8.8, T.Z43) — «Программа»: the row's RESOLVED course. Rows
   * whose course did not resolve match no course filter — a row outside every
   * programme is outside every programme slice.
   */
  courseId?: number;
  /** ADDITIVE (contract v1.8.8, T.Z43) — «Аудитория», the shared three values. */
  audience?: string;
  /**
   * ADDITIVE (contract v1.9.6, T.Z62) — «Группа»: a group id of #26.8, or the
   * `'none'` bucket for «без группы» (no live membership — every unmatched row
   * included). Resolved SERVER-side, composed with the other filters.
   */
  groupId?: number | typeof PREFILL_GROUP_NONE;
  page: number;
  pageSize: number;
}

/** The URL/wire literal of the «без группы» bucket (contract v1.9.6). */
export const PREFILL_GROUP_NONE = 'none';
/** ADDITIVE (contract v1.9.6, T.Z62) — the URL key of the «Группа» filter. */
export const PREFILL_GROUP_PARAM = 'groupId';

/** Every value #26.2's `?status=` answers: the match states + the funnel (v1.8.1). */
export type PrefillStatusFilter = 'NOT_FOUND' | 'AMBIGUOUS' | DashboardStatus;

/**
 * T.Z119 — the cabinet's own page size when neither the URL nor the user's stored
 * choice speaks: 20 rows, so the two children tables page before they scroll forever.
 * NOT the api's default (#26.2 answers 50 when `?pageSize=` is absent), which is why
 * {@link prefillQuery} always sends the resolved size unless it happens to equal the
 * api's own 50 — omitting 20 would silently grow the page back to 50.
 */
export const PREFILL_DEFAULT_PAGE_SIZE = 20;
/**
 * The api's own default (#26.2's server-side fallback, `dto.ts` `DEFAULT_PAGE_SIZE`).
 * The wire rule keys off THIS, not the web default: omitting a value equal to the
 * api's fallback is a no-op, any other size travels so the page is the one asked for.
 */
export const PREFILL_API_DEFAULT_PAGE_SIZE = 50;
/** The api's own cap (#26.2 clamps like #3/#6); the page-size select stops here. */
export const PREFILL_MAX_PAGE_SIZE = 200;

export const PREFILL_STATUS_PARAM = 'status';
/** ADDITIVE (contract v1.8.2) — the URL key of the working table's search box. */
export const PREFILL_SEARCH_PARAM = 'q';
/** ADDITIVE (contract v1.8.8, T.Z43) — «Программа» / «Аудитория» of the cabinet. */
export const PREFILL_COURSE_PARAM = 'courseId';
export const PREFILL_AUDIENCE_PARAM = 'audience';
/** The cabinet's own debounce for that box: narrowing is server-side, one read per pause. */
export const PREFILL_SEARCH_DEBOUNCE_MS = 300;
export const PAGE_PARAM = 'page';
export const PAGE_SIZE_PARAM = 'pageSize';

/**
 * Filters out of the URL's flat string map. Anything the api would answer 400 to
 * (an unknown `status`, a junk page) is dropped HERE rather than sent — a shared
 * or hand-edited link must not turn into an error screen.
 *
 * ADDITIVE (T.Z119) — `opts.defaultPageSize` is the per-user page size when the URL is
 * silent (the cabinet reads it from `localStorage`): a URL that does carry `?pageSize=`
 * still wins, so an old shared link keeps describing the view it was made for.
 */
export function parsePrefillFilters(
  params: Record<string, string>,
  opts: { defaultPageSize?: number } = {},
): PrefillFilters {
  const status = params[PREFILL_STATUS_PARAM];
  const q = params[PREFILL_SEARCH_PARAM];
  const page = readPositiveInt(params[PAGE_PARAM]) ?? 1;
  const pageSize = Math.min(
    readPositiveInt(params[PAGE_SIZE_PARAM]) ?? opts.defaultPageSize ?? PREFILL_DEFAULT_PAGE_SIZE,
    PREFILL_MAX_PAGE_SIZE,
  );
  const venueId = readPositiveInt(params.venueId);

  return {
    venueId,
    status: isPrefillStatusFilter(status) ? status : undefined,
    // Whitespace-only is no filter at all — the api treats an empty `q` as absent,
    // and the URL should never carry a search that searches nothing.
    q: readNonEmptyTrimmed(q),
    // ADDITIVE (v1.8.8, T.Z43) — a value the api would 400 never leaves the SPA.
    courseId: readPositiveInt(params[PREFILL_COURSE_PARAM]),
    audience: audienceFilterOf(params[PREFILL_AUDIENCE_PARAM] ?? '') ?? undefined,
    // ADDITIVE (v1.9.6, T.Z62) — a group id, the `none` bucket, or nothing: a stale or
    // hand-edited value is dropped here rather than sent as a 400.
    groupId: parsePrefillGroupFilter(params[PREFILL_GROUP_PARAM]),
    page,
    pageSize,
  };
}

/**
 * ADDITIVE (v1.9.6, T.Z62) — the «Группа» filter out of the URL: a positive integer,
 * the `'none'` bucket, or `undefined`. Anything else (a stale id, junk) is dropped —
 * the api would 400 it, and a shared link must not become an error screen.
 */
export function parsePrefillGroupFilter(raw: string | undefined): number | typeof PREFILL_GROUP_NONE | undefined {
  if (raw === undefined || raw === '') return undefined;
  if (raw === PREFILL_GROUP_NONE) return PREFILL_GROUP_NONE;
  return readPositiveInt(raw);
}

/**
 * The `?status=` values the api answers (contract v1.8.1). Kept literal rather
 * than derived from `DASHBOARD_STATUSES` so an unknown wire value still fails the
 * test and is dropped instead of being sent as a 400.
 */
const PREFILL_STATUS_FILTER_VALUES: ReadonlyArray<PrefillStatusFilter> = [
  'NOT_FOUND',
  'AMBIGUOUS',
  ...DASHBOARD_STATUSES,
];

/** True for a status the api actually filters on — anything else never leaves the SPA. */
export function isPrefillStatusFilter(value: string | undefined): value is PrefillStatusFilter {
  return value !== undefined && (PREFILL_STATUS_FILTER_VALUES as readonly string[]).includes(value);
}

/**
 * The «Статус» select — the WHOLE funnel from the one shared map (`STATUS_LABEL`,
 * T.R1), and nothing else (T.Z14/C6): the two match states left the UI. A single
 * `?status=` value cannot say «NOT_FOUND or AMBIGUOUS» (the contract takes ONE
 * enum literal — there is no multi-value capability on this parameter), so the
 * merged «Не найдено» bucket lives on the summary chips, which narrow the loaded
 * page by the very match state the row carries. A stale link with
 * `?status=NOT_FOUND` still parses and still narrows — the api answers it — but
 * nothing in the cabinet offers those values any more.
 */
export const PREFILL_STATUS_OPTIONS: ReadonlyArray<{ value: PrefillStatusFilter; label: string }> = DASHBOARD_STATUSES.map(
  (status) => ({ value: status as PrefillStatusFilter, label: STATUS_LABEL[status] }),
);

/**
 * The query string of #26.2, kept as the pure twin of `buildQuery` (`./client` is
 * not importable from plain Node). Empty values drop — the api treats an empty
 * parameter as absent, and the URL stays equal to its default-state form.
 */
export function prefillQuery(filters: PrefillFilters): Record<string, string | number | undefined> {
  return {
    venueId: filters.venueId,
    status: filters.status,
    q: filters.q,
    // ADDITIVE (v1.8.8, T.Z43) — the programme/audience pair, server-resolved like #3's.
    courseId: filters.courseId,
    audience: filters.audience,
    // ADDITIVE (v1.9.6, T.Z62) — the «Группа» filter, the literal riding as-is.
    groupId: filters.groupId,
    page: filters.page > 1 ? filters.page : undefined,
    // T.Z119 — the omission rule keys off the API's own default (50), not the web's:
    // the cabinet now asks for 20, and omitting 20 would let #26.2 answer its own 50.
    pageSize: filters.pageSize !== PREFILL_API_DEFAULT_PAGE_SIZE ? filters.pageSize : undefined,
  };
}

/** ADDITIVE (v1.9.5) — the numeric twin of {@link readPositiveInt} for wire values. */
export function readPositiveIntOrNull(raw: unknown): number | null {
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isFinite(value) && Number.isInteger(value) && value > 0 ? value : null;
}

export function readPositiveInt(raw: string | undefined): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

// ---------------------------------------------------------------------------
// Response parsing — the wire arrives as `unknown`, the shapes are asserted here
// ---------------------------------------------------------------------------

/** `#26` — `{data:{venues:[…]}}`. A row that is not shaped as agreed is dropped. */
export function parseCuratorVenues(body: unknown): CuratorVenue[] {
  const venues = pickList(body, 'venues');
  const out: CuratorVenue[] = [];
  for (const candidate of venues) {
    const venue = parseVenue(candidate);
    if (venue !== null) out.push(venue);
  }
  return out;
}

/** `#26.1` — `{data:{children:[…]}}`, a read-only roster, no pagination. */
export function parseCuratorChildren(body: unknown): CuratorChild[] {
  const children = pickList(body, 'children');
  const out: CuratorChild[] = [];
  for (const candidate of children) {
    if (typeof candidate !== 'object' || candidate === null) continue;
    const row = candidate as Record<string, unknown>;
    if (typeof row.currentStatus !== 'string') continue;
    // v1.9.32 (T.Z139) — `fullName` is OPTIONAL on the wire: a child the ЕВИ letter
    // has not reached has no name yet, and the api leaves the key OFF. Reading it as
    // `null` keeps the row (dropping it here would quietly shrink the roster back to
    // the pre-T.Z139 one); an old api's always-present string still reads as itself.
    out.push({
      fullName: typeof row.fullName === 'string' && row.fullName !== '' ? row.fullName : null,
      region: readStringOrNull(row.region),
      programName: readStringOrNull(row.programName),
      currentStatus: row.currentStatus,
      pguId: typeof row.pguId === 'string' ? row.pguId : '',
      untiId: readStringOrNull(row.untiId),
      applicationId: readPositiveIntOrNull(row.applicationId),
      group: parseGroupRef(row.group),
      // ADDITIVE (v1.9.64, T.Z195) — the chase facts, read only when the api sent a
      // well-shaped value: an older api (or a stub) leaves the keys off, which the
      // table renders as the quiet «—», never as a guessed fact.
      ...parseContractKeys(row),
    });
  }
  return out;
}

/** `#26.2` — `{data:{rows:[…]}, meta:{page,pageSize,total}}`. */
export function parseCuratorPrefillPage(body: unknown, meta: unknown): { rows: CuratorPrefillRow[]; meta: CuratorPageMeta | null } {
  const rows = pickList(body, 'rows');
  const out: CuratorPrefillRow[] = [];
  for (const candidate of rows) {
    const row = parsePrefillRow(candidate);
    if (row !== null) out.push(row);
  }
  return { rows: out, meta: parsePageMeta(meta) };
}

/** `#26.3`/`#26.5` — `{data:{row}}`. */
export function parseCuratorPrefillRow(body: unknown): CuratorPrefillRow | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  return 'row' in record ? parsePrefillRow(record.row) : parsePrefillRow(body);
}

function parsePrefillRow(candidate: unknown): CuratorPrefillRow | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const row = candidate as Record<string, unknown>;
  if (typeof row.id !== 'number' || typeof row.fullName !== 'string') return null;

  const parsed: CuratorPrefillRow = {
    id: row.id,
    fullName: row.fullName,
    nameFormatOk: row.nameFormatOk === true,
    region: readStringOrNull(row.region),
    programName: readStringOrNull(row.programName),
    pguId: readStringOrNull(row.pguId),
    untiId: readStringOrNull(row.untiId),
    // ADDITIVE (v1.9.6, T.Z62) — the group handle and the group itself; `null` is the
    // honest «без группы», an absent key reads as the same on an older envelope.
    applicationId: readPositiveIntOrNull(row.applicationId),
    group: parseGroupRef(row.group),
    lastCheckedAt: readStringOrNull(row.lastCheckedAt),
    // Server-computed (scope × status × flag): the UI hides a control, never
    // re-derives the rule (Р15/D3/D10 live in the api alone).
    canEdit: row.canEdit === true,
    canDelete: row.canDelete === true,
  };
  // ADDITIVE (v1.8.8, T.Z43) — null and a number are both answers; absent keeps the key off.
  if (typeof row.courseId === 'number' && Number.isInteger(row.courseId) && row.courseId > 0) {
    parsed.courseId = row.courseId;
  } else if (row.courseId === null) parsed.courseId = null;
  if (typeof row.currentStatus === 'string') parsed.currentStatus = row.currentStatus;
  if (row.matchStatus === 'NOT_FOUND' || row.matchStatus === 'AMBIGUOUS') parsed.matchStatus = row.matchStatus;
  if (typeof row.missReason === 'string' && isMissReason(row.missReason)) parsed.missReason = row.missReason;
  // ADDITIVE (v1.9.46, T.Z166) — the офлайн-flag's fact: present only when the api
  // sent one of its two literals or an honest null (an older api keeps the key off).
  if (row.matchedApplicationFormat === 'ONLINE' || row.matchedApplicationFormat === 'OFFLINE') {
    parsed.matchedApplicationFormat = row.matchedApplicationFormat;
  } else if (row.matchedApplicationFormat === null) parsed.matchedApplicationFormat = null;
  // ADDITIVE (v1.9.26, T.Z125) — the population marker; an absent key (an older api)
  // reads as the working row it always was.
  if (row.population === 'ONLINE_ROSTER' || row.population === 'PREFILL') parsed.population = row.population;
  // ADDITIVE (v1.9.83, T.Z227; AMENDED v1.9.84 — the same-day hotfix) — the contacts
  // of the ONLINE surface, read (defensively — a well-shaped value or an honest null,
  // junk dropped) on ANY row of this page: since v1.9.84 the boundary is the SURFACE,
  // not the population — an `'ONLINE_ROSTER'` row carries its own contacts, a MATCHED
  // `'PREFILL'` row the MATCHED child's (the owner's live check: the population-only
  // gate printed «—» on exactly the rows the curator looks at), `null` = nothing is
  // known. An absent key (every pre-v1.9.84 matched row on an older api) stays
  // absent, which the cells render as the quiet «—». The offline roster's parser
  // (`CuratorChild`) has no contact key at all — the «слепок» holds there.
  if (row.phone === null || typeof row.phone === 'string') parsed.phone = row.phone;
  if (row.email === null || typeof row.email === 'string') parsed.email = row.email;
  // ADDITIVE (v1.9.64, T.Z195) — the matched child's chase facts, the same
  // well-shaped-or-absent rule the offline roster reads them with.
  Object.assign(parsed, parseContractKeys(row));
  return parsed;
}

/**
 * ADDITIVE (v1.9.64, T.Z195) — the chase-fact keys one row carries, read defensively:
 * a `contractState` the api does not speak, a non-boolean flag or a junk day count is
 * dropped rather than rendered (an unknown fact is not a fact), and an ABSENT key (an
 * api that predates the columns) stays absent, which is the honest «no data here».
 */
function parseContractKeys(row: Record<string, unknown>): Pick<
  CuratorChild,
  'contractState' | 'contractSignedAt' | 'contractDeadlineAt' | 'contractDaysLeft' | 'enrolled' | 'eviFailed' | 'eviFailedManually'
> {
  const keys: Pick<
    CuratorChild,
    'contractState' | 'contractSignedAt' | 'contractDeadlineAt' | 'contractDaysLeft' | 'enrolled' | 'eviFailed' | 'eviFailedManually'
  > = {};
  if (
    row.contractState === 'SIGNED' ||
    row.contractState === 'NOT_SIGNED' ||
    row.contractState === 'NOT_ARRIVED' ||
    row.contractState === null
  ) {
    keys.contractState = row.contractState;
  }
  if (typeof row.contractSignedAt === 'string' || row.contractSignedAt === null) keys.contractSignedAt = row.contractSignedAt;
  if (typeof row.contractDeadlineAt === 'string' || row.contractDeadlineAt === null) {
    keys.contractDeadlineAt = row.contractDeadlineAt;
  }
  if (typeof row.contractDaysLeft === 'number' && Number.isFinite(row.contractDaysLeft)) {
    keys.contractDaysLeft = row.contractDaysLeft;
  } else if (row.contractDaysLeft === null) keys.contractDaysLeft = null;
  if (typeof row.enrolled === 'boolean' || row.enrolled === null) keys.enrolled = row.enrolled;
  if (typeof row.eviFailed === 'boolean') keys.eviFailed = row.eviFailed;
  if (typeof row.eviFailedManually === 'boolean') keys.eviFailedManually = row.eviFailedManually;
  return keys;
}

function parseVenue(candidate: unknown): CuratorVenue | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const venue = candidate as Record<string, unknown>;
  if (typeof venue.venueId !== 'number' || typeof venue.name !== 'string') return null;
  const summary = (typeof venue.summary === 'object' && venue.summary !== null ? venue.summary : {}) as Record<string, unknown>;
  return {
    venueId: venue.venueId,
    name: venue.name,
    region: readStringOrNull(venue.region),
    kind: venue.kind === 'PARTNER_ONLINE' ? 'PARTNER_ONLINE' : 'REGISTRY',
    allowCuratorChildren: venue.allowCuratorChildren === true,
    partner: parsePartner(venue.partner),
    summary: {
      offline: parseOfflineSummary(summary.offline),
      online: parseOnlineSummary(summary.online),
    },
    lastEviSnapshotAt: readStringOrNull(venue.lastEviSnapshotAt),
    lastUntiSyncAt: readStringOrNull(venue.lastUntiSyncAt),
  };
}

function parsePartner(candidate: unknown): CuratorPartnerRef | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const partner = candidate as Record<string, unknown>;
  if (typeof partner.code !== 'string') return null;
  return {
    code: partner.code,
    planOnline: readCount(partner.planOnline),
    used: readCount(partner.used),
  };
}

function parseOfflineSummary(candidate: unknown): CuratorOfflineSummary | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const summary = candidate as Record<string, unknown>;
  return { planOffline: readCount(summary.planOffline), offlineAssigned: readCount(summary.offlineAssigned) };
}

function parseOnlineSummary(candidate: unknown): CuratorOnlineSummary | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const summary = candidate as Record<string, unknown>;
  return {
    prefillTotal: readCount(summary.prefillTotal),
    found: readCount(summary.found),
    notFound: readCount(summary.notFound),
    ambiguous: readCount(summary.ambiguous),
    // ADDITIVE (v1.9.26, T.Z125) — `0` for a key an older api omits: the summary then
    // speaks the working set alone, exactly what that api's table answers.
    onlineAssigned: readCount(summary.onlineAssigned),
    planOnline: readCount(summary.planOnline),
  };
}

function parsePageMeta(candidate: unknown): CuratorPageMeta | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const meta = candidate as Record<string, unknown>;
  if (typeof meta.total !== 'number' || typeof meta.page !== 'number') return null;
  return {
    page: meta.page,
    pageSize: typeof meta.pageSize === 'number' ? meta.pageSize : PREFILL_DEFAULT_PAGE_SIZE,
    total: meta.total,
    // ADDITIVE (v1.9.46, T.Z166) — the notice's count, kept only when the api sent a
    // sane number (an older api keeps the key off and the notice stays hidden).
    ...(typeof meta.offlineFormatMatched === 'number' && Number.isFinite(meta.offlineFormatMatched) && meta.offlineFormatMatched >= 0
      ? { offlineFormatMatched: meta.offlineFormatMatched }
      : {}),
  };
}

export function pickList(body: unknown, key: string): unknown[] {
  if (typeof body !== 'object' || body === null) return [];
  const record = body as Record<string, unknown>;
  // Both the raw envelope `{venues:[…]}` and the unwrapped `{data:{venues:[…]}}` land here.
  const source = Array.isArray(record[key]) ? record[key] : readNestedList(record.data, key);
  return Array.isArray(source) ? source : [];
}

function readNestedList(source: unknown, key: string): unknown {
  if (typeof source !== 'object' || source === null) return undefined;
  const value = (source as Record<string, unknown>)[key];
  return Array.isArray(value) ? value : undefined;
}

export function readStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/** ADDITIVE (v1.9.6, T.Z62) — the row's `group` ref: both keys, or `null`. */
function parseGroupRef(candidate: unknown): CuratorGroupRef | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const ref = candidate as Record<string, unknown>;
  const id = readPositiveIntOrNull(ref.id);
  if (id === null || typeof ref.name !== 'string') return null;
  return { id, name: ref.name };
}

function readNonEmptyTrimmed(raw: string | undefined): string | undefined {
  const trimmed = raw?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

function isMissReason(value: string): value is CuratorMissReason {
  return value === 'NO_ID_AND_NO_NAME_HIT' || value === 'REGION_UNMAPPED' || value === 'NAME_FORMAT' || value === 'AMBIGUOUS';
}

// ---------------------------------------------------------------------------
// D6 — the section split BY ASSIGNMENT
// ---------------------------------------------------------------------------

/** The two halves of the summary, derived from the venue SET (D6), not from a label. */
export interface CabinetSections {
  /** Venues with an offline half (`kind = 'REGISTRY'` ⇒ `summary.offline`). */
  offline: CuratorVenue[];
  /** Venues with the prefill flag ⇒ `summary.online`. */
  online: CuratorVenue[];
}

export function splitByAssignment(venues: readonly CuratorVenue[]): CabinetSections {
  const offline: CuratorVenue[] = [];
  const online: CuratorVenue[] = [];
  for (const venue of venues) {
    if (venue.summary.offline !== null) offline.push(venue);
    if (venue.summary.online !== null) online.push(venue);
  }
  return { offline, online };
}

/**
 * The partner pool, read ONCE: the pool belongs to the PARTNER (Р5), so every
 * sibling branch reports the same `planOnline` and the same `used`. The first
 * venue that carries one speaks for all of them; `null` when the scope has no
 * partner venue (an offline-only curator gets no pool block at all).
 */
export function partnerPool(venues: readonly CuratorVenue[]): CuratorPartnerRef | null {
  for (const venue of venues) {
    if (venue.partner !== null) return venue.partner;
  }
  return null;
}

/** `true` when at least one venue of the scope allows the prefill writes (D3). */
export function hasPrefillVenue(venues: readonly CuratorVenue[]): boolean {
  return venues.some((venue) => venue.allowCuratorChildren);
}

/**
 * The «Скачать CSV» button exists where the online section exists: the export is
 * the prefill projection, and an offline-only scope has nothing to export (SR §5.2).
 */
export function canExportCsv(venues: readonly CuratorVenue[]): boolean {
  return hasPrefillVenue(venues);
}

/** The venue a venue-scoped control defaults to: the first of the scope. */
export function defaultVenueId(venues: readonly CuratorVenue[]): number | null {
  return venues[0]?.venueId ?? null;
}

// ---------------------------------------------------------------------------
// D5/D11 — the status column, and the miss-reason wording
// ---------------------------------------------------------------------------

export type RowStatusTone = 'neutral' | 'brand' | 'warn' | 'ok' | 'bad';

export interface RowStatusView {
  /** What the cell says — never a bare «найдено»: a matched row shows its system status. */
  label: string;
  tone: RowStatusTone;
  /** The miss reason, when the row is unmatched and the matcher named one. */
  missReasonLabel: string | null;
}

/**
 * THE status rule of the cabinet (D5/D11, merged per T.Z14/C6):
 *
 *   - unmatched  → «не найдено» (neutral) + the miss reason beside it,
 *   - ambiguous  → ALSO «не найдено» — the UI keeps ONE unmatched bucket. The word
 *                  «неоднозначно» left the cabinet: a curator reads one queue and
 *                  fixes rows, and the difference between «no hit» and «several
 *                  hits» lives in the miss-reason hint (`AMBIGUOUS` →
 *                  «Неоднозначное совпадение») when the api names one,
 *   - matched    → the child's CURRENT system status, and NO «найдено» badge:
 *                  the identifiers and the status already say it.
 */
export function rowStatusView(row: CuratorPrefillRow): RowStatusView {
  if (row.currentStatus !== undefined) {
    return { label: statusLabelOf(row.currentStatus), tone: statusToneOf(row.currentStatus), missReasonLabel: null };
  }
  return { label: 'не найдено', tone: 'neutral', missReasonLabel: missReasonLabel(row.missReason) };
}

/**
 * The system status in RU. `STATUS_LABEL` (pages/dashboard/status.ts) is the ONE
 * vocabulary of the app — the cabinet reuses it rather than growing a second map.
 * A status this build does not know prints as its code, never as an empty cell.
 */
export function statusLabelOf(status: string): string {
  return isKnownStatus(status) ? STATUS_LABEL[status] : status;
}

/** Same tone semantics the dashboard badges use. */
export function statusToneOf(status: string): RowStatusTone {
  return isKnownStatus(status) ? STATUS_TONE[status] : 'neutral';
}

function isKnownStatus(status: string): status is DashboardStatus {
  return (DASHBOARD_STATUSES as readonly string[]).includes(status);
}

/** «что именно чинить» — the RU hint of every coarse miss reason (SR §3.4). */
export const MISS_REASON_LABEL: Readonly<Record<CuratorMissReason, string>> = {
  NO_ID_AND_NO_NAME_HIT: 'Не найден по ФИО и ID',
  REGION_UNMAPPED: 'Регион не сопоставлен',
  NAME_FORMAT: 'Формат ФИО',
  AMBIGUOUS: 'Неоднозначное совпадение',
};

export function missReasonLabel(reason: CuratorMissReason | undefined): string | null {
  return reason === undefined ? null : MISS_REASON_LABEL[reason];
}

// ---------------------------------------------------------------------------
// #26.6 — the export request, one pure function so the unit project can pin it
// ---------------------------------------------------------------------------

/** The two files #26.6 answers; `csv` is the api's default, `xlsx` the button's. */
export type CuratorExportFormat = 'csv' | 'xlsx';

/**
 * ADDITIVE (v1.8.9, T.Z47) — the sections #26.6 streams. `offline` names the venue's
 * #26.1 roster instead of the prefill rows; the api then REQUIRES the `venueId`.
 */
export type CuratorExportSection = 'offline';

/**
 * The query of #26.6. `format=xlsx` is the button's own choice (T.Y18); `venueId`
 * (ADDITIVE v1.8.2) is the SCOPED export (T.Z14/Z5): a specific venue in the picker
 * narrows the file to that venue — the same rows its table would show — while «Все
 * места» sends none and gets every flagged venue of the scope, the v1.8.1 answer.
 * The api scope-checks the id (403 on a foreign one); the SPA only has to send the
 * one it is looking at.
 *
 * ADDITIVE (v1.8.9, T.Z47) — `section: 'offline'` asks for the OFFLINE ROSTER of
 * that venue (the «Скачать XLSX» of the #26.1 table): the api answers with the same
 * six columns the table renders and 403/400 exactly like #26.1 does when the venue
 * is foreign, unknown or has no offline section — so the caller ALWAYS sends the
 * venueId it is looking at (the button lives in that venue's block header).
 */
export function curatorExportQuery(format: CuratorExportFormat = 'xlsx', venueId?: number, section?: CuratorExportSection): string {
  const query = new URLSearchParams();
  if (format !== 'csv') query.set('format', format);
  if (venueId !== undefined) query.set('venueId', String(venueId));
  if (section !== undefined) query.set('section', section);
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

// ---------------------------------------------------------------------------
// Refusals — the RU copy of every code the cabinet can meet
// ---------------------------------------------------------------------------

/**
 * The api's own sentences, one level up. `PREFILL_FIO_INVALID` and
 * `PREFILL_QUOTA_EXCEEDED` read their structured `meta` (`hint`, `limit`); the
 * rest keep the server's message when it has one, because the api is the
 * authority on why a write was refused.
 */
export function curatorErrorCopy(error: unknown): string {
  if (typeof error !== 'object' || error === null) return 'Не удалось выполнить действие. Попробуйте ещё раз.';
  const candidate = error as { code?: unknown; status?: number; message?: unknown; metaNumber?: (key: string) => number | undefined };
  const code = typeof candidate.code === 'string' ? candidate.code : undefined;
  const message = typeof candidate.message === 'string' && candidate.message.trim() !== '' ? candidate.message : undefined;
  const metaNumber = typeof candidate.metaNumber === 'function' ? candidate.metaNumber : () => undefined;

  switch (code) {
    case 'PREFILL_FIO_INVALID': {
      const hint = readMetaText(error, 'hint');
      // Without the api's own hint the fallback names the format example the
      // contract ships (SR §A13) — never a real-looking person.
      return hint === null
        ? 'ФИО должно состоять минимум из двух слов буквами: Фамилия Имя Отчество.'
        : `ФИО должно состоять минимум из двух слов буквами. ${hint}`;
    }
    case 'PREFILL_NOT_ALLOWED':
      return 'На этой площадке внесение строк куратором отключено. Обратитесь к администратору.';
    case 'PREFILL_ROW_IMMUTABLE':
      return 'Строку нельзя изменить: она уже сопоставлена или закреплена за другой площадкой.';
    case 'PREFILL_QUOTA_EXCEEDED': {
      const limit = metaNumber('limit');
      return limit === undefined
        ? 'Пул партнёра исчерпан — новая строка не помещается в лимит.'
        : `Пул партнёра исчерпан (${limit}).`;
    }
    case 'CURATOR_SCOPE_REQUIRED':
      return 'Этот раздел недоступен вашей учётной записи: кабинет показывает только ваши площадки.';
    case 'VALIDATION':
      return message ?? 'Проверьте правильность заполнения полей.';
    case 'GROUP_FULL': {
      // T.Z50 — the owner's answer: the cap is HARD. The api's `meta.limit` is the
      // same 30 the group DTO carries; the copy names the way out, not the code.
      const limit = metaNumber('limit');
      return limit === undefined
        ? 'В группе уже максимум участников — уберите кого-то, чтобы добавить нового.'
        : `В группе уже максимум участников (${limit}) — уберите кого-то, чтобы добавить нового.`;
    }
    case 'GROUP_NOT_EMPTY':
      return 'Сначала уберите из группы всех участников — группу с составом удалить нельзя.';
    case 'GROUP_NAME_TAKEN':
      return 'Группа с таким названием уже есть на этой площадке.';
    case 'GROUP_MEMBER_EXISTS':
      return 'Этот ученик уже состоит в группе.';
    case 'CHILD_ALREADY_IN_GROUP': {
      // ADDITIVE (v1.9.14, T.Z82) — ONE child = ONE group: the api names the group the
      // child is ALREADY in (`meta.groupName`), and the copy names the way out — a
      // move is remove-then-add, never a second membership. Without the meta (an older
      // api, a hand-written refusal) the rule still says itself.
      const groupName = readMetaText(error, 'groupName');
      return groupName === null
        ? 'Ученик уже состоит в другой группе — уберите его оттуда, чтобы перенести.'
        : `Ученик уже состоит в группе «${groupName}» — уберите его оттуда, чтобы перенести.`;
    }
    case 'PREFILL_OFFLINE_FORMAT':
      // ADDITIVE (contract v1.9.46, T.Z166) — the upload guard on #26.3: the strong
      // key the body carries names an OFFLINE application. The api's `meta.venueName`
      // is the площадка whose офлайн-таблице the child already stands in — the same
      // «the refusal carries the sentence» rule `CHILD_ALREADY_IN_GROUP` keeps.
      {
        const venueName = readMetaText(error, 'venueName');
        return venueName === null
          ? 'Ребёнок уже в офлайн-таблице (офлайн-формат) — уберите его из онлайн-набора.'
          : `Ребёнок уже в офлайн-таблице площадки «${venueName}» (офлайн-формат) — уберите его из онлайн-набора.`;
      }
    case 'CONFLICT':
      // T.Z24 — the server's own duplicate refusal (the `curator_children_natural_key`
      // partial unique index, migration 0017, on a LIVE row of the same venue) reads
      // as the same hard block the drawer's own gate shows, plus the one fact only
      // the server knows: the twin it hit is on THIS venue. A row that slips past
      // the gate's read (a race between the read and the write) lands here.
      return `${PREFILL_DUPLICATE_BLOCK_MESSAGE} Такая строка уже есть на этой площадке.`;
    default:
      return message ?? 'Не удалось выполнить действие. Попробуйте ещё раз.';
  }
}

function readMetaText(error: unknown, key: string): string | null {
  const meta = (error as { meta?: unknown }).meta;
  if (typeof meta !== 'object' || meta === null) return null;
  const value = (meta as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

// ---------------------------------------------------------------------------
// #26.7 — the «Загрузить из файла» report (ADDITIVE v1.8.9, T.Z46; the three-section
// modal and the widened already-found detection are T.Z63)
// ---------------------------------------------------------------------------

export const CURATOR_PREFILL_IMPORT_PATH = '/curator/prefill/import';

/** The file picker's own filter, and the cap the api answers 400 over (the #27 number). */
export const PREFILL_IMPORT_ACCEPT = '.xlsx,.csv';
export const PREFILL_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

export type CuratorPrefillImportOutcome = 'IMPORTED' | 'DUPLICATE' | 'REJECTED';

/**
 * One line of the report — the verdict (row number, outcome, reason, flags) plus, since
 * v1.9.10, the row's own parsed cells echoed back to the uploader (an absent key = an
 * empty cell in the file).
 */
export interface CuratorPrefillImportRow {
  line: number;
  outcome: CuratorPrefillImportOutcome;
  reason?: string;
  duplicateOfLine?: number | null;
  flags?: string[];
  fullName?: string;
  pguId?: string;
  untiId?: string;
  region?: string;
  programName?: string;
  /**
   * ADDITIVE (contract v1.9.46, T.Z166) — on an `OFFLINE_FORMAT` refusal: the площадка
   * the OFFLINE application the row's id names already stands on («ребёнок уже в
   * офлайн-таблице этой площадки»). Absent when the application is venue-less or the
   * reason is a different one. An organisation label, never a contact — the report
   * stays PII-free in the minors' sense.
   */
  venueName?: string;
}

/** The answer of #26.7 — the per-row feedback the owner asked for. */
export interface CuratorPrefillImportReport {
  venueId: number;
  fileName: string | null;
  fileKind: 'xlsx' | 'csv';
  rowsTotal: number;
  imported: number;
  duplicates: number;
  rejected: number;
  flagged: number;
  withId: number;
  withoutId: number;
  courseResolved: number;
  courseUnresolved: number;
  rows: CuratorPrefillImportRow[];
}

const IMPORT_OUTCOMES: readonly string[] = ['IMPORTED', 'DUPLICATE', 'REJECTED'];

/**
 * The defensive read of #26.7's answer. The report is data the curator acts on, so a
 * half-shaped body degrades to `null` (an honest «ответ не распознан») instead of
 * throwing mid-render — the same posture every parse helper in this file takes.
 */
export function parseCuratorPrefillImportReport(value: unknown): CuratorPrefillImportReport | null {
  if (typeof value !== 'object' || value === null) return null;
  const source = value as Record<string, unknown>;
  const numberOf = (key: string): number | null => {
    const raw = source[key];
    return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : null;
  };
  const counts = ['rowsTotal', 'imported', 'duplicates', 'rejected', 'flagged', 'withId', 'withoutId', 'courseResolved', 'courseUnresolved']
    .map(numberOf);
  if (counts.some((count) => count === null)) return null;
  if (typeof source['venueId'] !== 'number') return null;
  if (!Array.isArray(source['rows'])) return null;

  const rows: CuratorPrefillImportRow[] = [];
  for (const entry of source['rows']) {
    if (typeof entry !== 'object' || entry === null) return null;
    const row = entry as Record<string, unknown>;
    const line = row['line'];
    const outcome = row['outcome'];
    if (typeof line !== 'number' || typeof outcome !== 'string' || !IMPORT_OUTCOMES.includes(outcome)) return null;
    rows.push({
      line,
      outcome: outcome as CuratorPrefillImportOutcome,
      ...(typeof row['reason'] === 'string' ? { reason: row['reason'] } : {}),
      ...(typeof row['duplicateOfLine'] === 'number' ? { duplicateOfLine: row['duplicateOfLine'] as number } : {}),
      ...(Array.isArray(row['flags'])
        ? { flags: row['flags'].filter((flag): flag is string => typeof flag === 'string') }
        : {}),
      // v1.9.10 — the row's own cells, echoed to the uploader. A key the api left off
      // (an empty cell) stays off here too; anything that is not a string is dropped.
      ...readCell(row, 'fullName'),
      ...readCell(row, 'pguId'),
      ...readCell(row, 'untiId'),
      ...readCell(row, 'region'),
      ...readCell(row, 'programName'),
      // ADDITIVE (v1.9.46, T.Z166) — the офлайн refusal's площадка.
      ...readCell(row, 'venueName'),
    });
  }

  return {
    venueId: source['venueId'],
    fileName: typeof source['fileName'] === 'string' ? source['fileName'] : null,
    fileKind: source['fileKind'] === 'csv' ? 'csv' : 'xlsx',
    rowsTotal: counts[0] as number,
    imported: counts[1] as number,
    duplicates: counts[2] as number,
    rejected: counts[3] as number,
    flagged: counts[4] as number,
    withId: counts[5] as number,
    withoutId: counts[6] as number,
    courseResolved: counts[7] as number,
    courseUnresolved: counts[8] as number,
    rows,
  };
}

/** One echoed cell of a report row (v1.9.10) — kept only when the api sent a string. */
function readCell(row: Record<string, unknown>, key: string): Partial<Record<string, string>> {
  const value = row[key];
  return typeof value === 'string' && value !== '' ? { [key]: value } : {};
}

/** The content columns of the report's per-row tables, in the file's own order —
 *  the same five captions the modal's description line names (T.Z74: the fifth is
 *  «Программа/Курс», the owner's wording, not the bare «Программа»). */
export const IMPORT_CELL_COLUMNS: ReadonlyArray<readonly [keyof CuratorPrefillImportRow, string]> = [
  ['fullName', 'ФИО'],
  ['pguId', 'Номер заявления'],
  ['untiId', 'УНТИ ID'],
  ['region', 'Регион'],
  ['programName', 'Программа/Курс'],
];

/** The printable form of an echoed cell — an empty one reads as a dash, never blank. */
export function importCellText(row: CuratorPrefillImportRow, key: keyof CuratorPrefillImportRow): string {
  const value = row[key];
  return typeof value === 'string' && value !== '' ? value : '—';
}

/** The flags an IMPORTED row can carry — what the curator should fix on that row.
 *  `NAME_FORMAT` stays readable for an older answer, but since T.Z63 the file door
 *  refuses such a row (a REJECTED reason) instead of importing it flagged. */
export const IMPORT_FLAG_LABEL: Readonly<Record<string, string>> = {
  NAME_FORMAT: 'ФИО не в формате',
  COURSE_UNRESOLVED: 'Программа не распознана',
};

/** Why a row was skipped (DUPLICATE) or refused (REJECTED). T.Z63 added the three
 *  looser already-found matches to the natural key, and made the bad ФИО a refusal. */
export const IMPORT_REASON_LABEL: Readonly<Record<string, string>> = {
  DUPLICATE_NATURAL_KEY: 'Такая строка уже есть',
  DUPLICATE_PGU_ID: 'Номер заявления уже есть',
  DUPLICATE_UNTI_ID: 'УНТИ ID уже есть',
  DUPLICATE_FIO: 'Ребёнок с таким ФИО уже есть',
  MISSING_FULL_NAME: 'ФИО не заполнено',
  NAME_FORMAT: 'ФИО не в формате',
  BAD_PGU_ID: 'Номер заявления не в формате',
  BAD_UNTI_ID: 'УНТИ ID не в формате',
  // ADDITIVE (contract v1.9.46, T.Z166) — the офлайн guard: the row's «номер
  // заявления»/УНТИ id names a child whose application is OFFLINE, i.e. the child
  // already stands in an офлайн-таблице. The report's Пояснение appends the площадка
  // (`venueName`) when the api sent one.
  OFFLINE_FORMAT: 'Ребёнок уже в офлайн-таблице (офлайн-формат)',
};

/** The outcome's word in the report table. */
export const IMPORT_OUTCOME_LABEL: Readonly<Record<CuratorPrefillImportOutcome, string>> = {
  IMPORTED: 'Загружено',
  DUPLICATE: 'Уже найдено',
  REJECTED: 'Ошибка',
};

/** One of the modal's three buckets (T.Z63): the report grouped by outcome, in the
 *  owner's order — «Уже найденные», «Загруженные», «С ошибками». */
export interface ImportReportSection {
  key: 'found' | 'imported' | 'errors';
  /** The section title, the owner's wording verbatim. */
  title: string;
  /** The outcome the bucket groups — the badge's tone rides on it. */
  outcome: CuratorPrefillImportOutcome;
  count: number;
  rows: CuratorPrefillImportRow[];
}

/**
 * The report as the three collapsed sections the modal renders. `rows` keeps the
 * api's file order inside every bucket, and the three counts always add up to
 * `rowsTotal` — the identity the api pins (a row lands in exactly one bucket).
 */
export function importReportSections(report: CuratorPrefillImportReport): ImportReportSection[] {
  const buckets: Record<CuratorPrefillImportOutcome, CuratorPrefillImportRow[]> = {
    DUPLICATE: [],
    IMPORTED: [],
    REJECTED: [],
  };
  for (const row of report.rows) {
    if (row.outcome in buckets) buckets[row.outcome].push(row);
  }
  return [
    { key: 'found', title: 'Уже найденные', outcome: 'DUPLICATE', count: report.duplicates, rows: buckets.DUPLICATE },
    { key: 'imported', title: 'Загруженные', outcome: 'IMPORTED', count: report.imported, rows: buckets.IMPORTED },
    { key: 'errors', title: 'С ошибками', outcome: 'REJECTED', count: report.rejected, rows: buckets.REJECTED },
  ];
}

/**
 * The one-line verdict the modal shows above the three sections — the totals header
 * the owner asked for (`rowsTotal` = найденные + загруженные + ошибки). The flagged
 * count rides along only when it is non-zero — «2 с пометкой» is information, «0 с
 * пометкой» is noise.
 */
export function importSummaryLine(report: CuratorPrefillImportReport): string {
  const parts = [`уже найдено ${report.duplicates}`, `загружено ${report.imported}`, `с ошибками ${report.rejected}`];
  if (report.flagged > 0) parts.push(`с пометкой ${report.flagged}`);
  return `${importFileNoun(report)}: ${parts.join(', ')}.`;
}

function importFileNoun(report: CuratorPrefillImportReport): string {
  return `Строк в файле ${report.rowsTotal} — ${ruPluralWord(report.rowsTotal, 'обработана', 'обработаны', 'обработано')}`;
}

/** The word alone — «строка/строки/строк» — for a sentence that writes its own count. */
export function ruPluralWord(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** «1 строка / 2 строки / 5 строк» — the count and its word, no library. */
export function ruPlural(count: number, one: string, few: string, many: string): string {
  return `${count} ${ruPluralWord(count, one, few, many)}`;
}

/**
 * The client-side pre-check the drawer runs before a round trip: an extension the
 * picker should have caught and a size the api would refuse anyway. `null` = the
 * file may go. The api stays the authority; this only saves the curator a wait.
 */
export function prefillImportFileError(file: { name: string; size: number }): string | null {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.xlsx') && !name.endsWith('.csv')) {
    return 'Подойдёт файл .xlsx или .csv.';
  }
  if (file.size > PREFILL_IMPORT_MAX_BYTES) {
    return `Файл больше 5 МБ — разбейте его на части.`;
  }
  if (file.size === 0) {
    return 'Файл пустой.';
  }
  return null;
}

/**
 * The one-line notice the BLOCK shows once a #26.7 file has landed (the modal keeps
 * its own, fuller three-section report). Same facts, one sentence, no second counting
 * rule.
 */
export function importNoticeLine(report: CuratorPrefillImportReport): string {
  const parts = [`${report.imported} ${ruPluralWord(report.imported, 'строка', 'строки', 'строк')} загружено`];
  if (report.duplicates > 0) parts.push(`уже найдено ${report.duplicates}`);
  if (report.rejected > 0) parts.push(`с ошибками ${report.rejected}`);
  if (report.flagged > 0) parts.push(`с пометкой ${report.flagged}`);
  return `Файл обработан: ${parts.join(', ')}.`;
}
